import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth'
import { api, ApiError } from '../api'
import { useI18n, LangToggle } from '../i18n'
import { Alert, FullLoader, Spinner } from '../components/ui'

export default function SetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const { setUser } = useAuth()
  const { t } = useI18n()

  const [state, setState] = useState<'checking' | 'ready' | 'invalid'>('checking')
  const [email, setEmail] = useState('')
  const [returning, setReturning] = useState(false)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) {
      setState('invalid')
      return
    }
    api
      .verifyToken(token)
      .then((res) => {
        setEmail(res.email)
        setName(res.name)
        setReturning(res.hasPassword)
        setState('ready')
      })
      .catch(() => setState('invalid'))
  }, [token])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (password.length < 8) {
      setError(t('setpw.error.length'))
      return
    }
    if (password !== confirm) {
      setError(t('setpw.error.match'))
      return
    }
    setBusy(true)
    try {
      const { user } = await api.setPassword(token, password, name.trim())
      setUser(user)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('setpw.error.generic'))
      setBusy(false)
    }
  }

  if (state === 'checking') return <FullLoader />

  if (state === 'invalid') {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-brand">
            <img className="brand-logo" src="/mark.png" alt="Armon Kappeli" />
            <div className="brand-name">Armon Kappeli</div>
          </div>
          <h1>{t('setpw.invalid.title')}</h1>
          <p className="sub">{t('setpw.invalid.body')}</p>
          <button className="btn btn-primary btn-block" onClick={() => navigate('/login')}>
            {t('login.back')}
          </button>
          <div className="auth-lang"><LangToggle /></div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          <img className="brand-logo" src="/mark.png" alt="Armon Kappeli" />
          <div className="brand-name">Armon Kappeli</div>
        </div>
        <h1>{returning ? t('setpw.title.reset') : t('setpw.title.new')}</h1>
        <p className="sub">
          {returning ? t('setpw.sub.reset', { email }) : t('setpw.sub.new', { email })}
        </p>
        <form className="auth-form" onSubmit={onSubmit}>
          {error && <Alert kind="error">{error}</Alert>}
          <div className="field">
            <label htmlFor="name">{t('setpw.name')}</label>
            <input
              id="name"
              className="input"
              type="text"
              placeholder={t('setpw.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="field">
            <label htmlFor="pw">{t('setpw.password')}</label>
            <input
              id="pw"
              className="input"
              type="password"
              placeholder={t('setpw.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="pw2">{t('setpw.confirm')}</label>
            <input
              id="pw2"
              className="input"
              type="password"
              placeholder={t('setpw.confirmPlaceholder')}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
            {busy ? <Spinner /> : t('setpw.submit')}
          </button>
        </form>
        <div className="auth-lang"><LangToggle /></div>
      </div>
    </div>
  )
}
